import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

function useGuestWishlist() {
  const queryClient = useQueryClient();

  // Get guest wishlist from localStorage
  const { data: guestWishlist = [] } = useQuery({
    queryKey: ["guestWishlist"],
    queryFn: () => {
      const savedWishlist = localStorage.getItem("guestWishlist");
      return savedWishlist ? JSON.parse(savedWishlist) : [];
    },
  });

  // Add to wishlist mutation
  const { mutate: addToWishlist } = useMutation({
    mutationFn: (productData) => {
      if (
        guestWishlist.some((item) => item.productId === productData.productId)
      ) {
        throw new Error("Product already in wishlist");
      }
      const updatedWishlist = [
        ...guestWishlist,
        {
          productId: productData.productId,
          priceAtAdded: productData.priceAtAdded,
        },
      ];
      localStorage.setItem("guestWishlist", JSON.stringify(updatedWishlist));
      return updatedWishlist;
    },
    onSuccess: (updatedWishlist) => {
      queryClient.setQueryData(["guestWishlist"], updatedWishlist);
      toast.success("Added to wishlist");
    },
    onError: (error) => {
      if (error.message === "Product already in wishlist") {
        toast.error("Product already in wishlist");
      } else {
        toast.error("Failed to add to wishlist");
      }
    },
  });

  // Remove from wishlist mutation
  const { mutate: removeFromWishlist } = useMutation({
    mutationFn: (productId) => {
      const updatedWishlist = guestWishlist.filter(
        (item) => item.productId !== productId
      );
      localStorage.setItem("guestWishlist", JSON.stringify(updatedWishlist));
      return updatedWishlist;
    },
    onSuccess: (updatedWishlist) => {
      queryClient.setQueryData(["guestWishlist"], updatedWishlist);
      toast.success("Removed from wishlist");
    },
    onError: () => {
      toast.error("Failed to remove from wishlist");
    },
  });

  // Clear wishlist mutation
  const { mutate: clearWishlist } = useMutation({
    mutationFn: () => {
      localStorage.removeItem("guestWishlist");
      return [];
    },
    onSuccess: () => {
      queryClient.setQueryData(["guestWishlist"], []);
      toast.success("Wishlist cleared");
    },
    onError: () => {
      toast.error("Failed to clear wishlist");
    },
  });

  const isInWishlist = (productId) => {
    return guestWishlist.some((item) => item.productId === productId);
  };

  return {
    guestWishlist,
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    isInWishlist,
  };
}

export default useGuestWishlist;
